﻿import React, { useState, useEffect, useCallback } from 'react';
import { Intent, Spinner } from '@blueprintjs/core';
import { NotificationToaster, AlertUnsavedData, NotificationInline } from "components/notifications";
import { ButtonSave } from "components/buttons";
import { PolicySettings, PolicyDocumentSettings } from 'components/settings';
import { EmployeeService } from "services";
import { useDirtyData } from "hooks/useDirtyData";
import moment from "moment";
import { PolicyDetails } from 'components/settings/PolicyDetails';
import { PolicyAdditionalSettings } from 'components/settings/PolicyAdditionalSettings';
import _ from 'underscore';

export function PolicyEdit(props) {

    const [originalPolicySettings, setOriginalPolicySettings] = useState();
    const [policySettingValid, setPolicySettingValid] = useState(true);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [reoccurrenceSettingsDisabled, setReoccurrenceSettingsDisabled] = useState(false);

    const [policySettings, setPolicySettings] = useState({
        // Details
        name: "",
        employeeTypes: [],
        employeeCategorys: [],
        useAllLocations: true,
        locations: [],
        // Settings
        resendSchedule: {
            reoccurring: false
        },
        resendPeriodId: null,
        sendDebriefAlert: true,
        debriefAlertDelayDays: 0,
        // Aditional Settings
        startDate: "",
        endDate: "",
        policyDocuments: []
    });
    const isDataDirty = useDirtyData(originalPolicySettings, policySettings);

    useEffect(() => {
        if (props.policyId != null && props.policyId > 0) {
            EmployeeService.getPolicy(props.policyId).then(
                (response) => {
                    setPolicySettings(response);
                    setOriginalPolicySettings(_.extend({}, response));
                    setLoading(false);
                    updatePolicyState(response);
                },
                () => {
                    setSaving(false);
                    NotificationToaster.show(Intent.DANGER, "Could not load policy. Please try again.");
                }
            );
        }
    }, [props.policyId]);

    function updatePolicyState(policy) {
        if (policy.endDate && moment(policy.endDate).isBefore(moment(), 'day')) {
            setDisabled(true);
        } else {
            setDisabled(false);
        }

        if (policy.startDate && moment(policy.startDate).isBefore(moment(), 'minute')) {
            setReoccurrenceSettingsDisabled(true);
        } else {
            setReoccurrenceSettingsDisabled(false);
        }
    }

    function onPolicySettingValidationChange(isValid) {
        setPolicySettingValid(isValid);
    }

    function onPolicySettingsChange(item) {
        setPolicySettings({
            ...policySettings,
            ...item
        })
    }

    function savePolicy() {
        setSaving(true);

        if (!policySettingValid) {
            NotificationToaster.show(Intent.WARNING, "There are errors on the page that need your attention");
            setSaving(false);
            return;
        }

        EmployeeService.savePolicy(policySettings).then(
            () => {
                setSaving(false);
                setOriginalPolicySettings(policySettings);
                NotificationToaster.show(Intent.SUCCESS, props.show + " updated successfully.");
                updatePolicyState(policySettings);

            },
            () => {
                setSaving(false);
                NotificationToaster.show(Intent.DANGER, "Could not update the " + props.show + " Please try again.");
            }
        );
    }

    const Tabs = useCallback((tabsProps) => {
        if (loading) {
            return <Spinner size={25} />;
        }

        const disableEditing = disabled || saving;

        switch (tabsProps.show) {
            case 'Policy Details':
                return (
                    <PolicyDetails
                        saving={disableEditing}
                        loading={loading}
                        policy={tabsProps.policySettings}
                        onSettingsUpdate={onPolicySettingsChange}
                        onValidationUpdate={onPolicySettingValidationChange}
                    />
                );
            case 'Settings':
                return (
                    <PolicySettings
                        saving={disableEditing}
                        loading={loading}
                        policy={tabsProps.policySettings}
                        onSettingsUpdate={onPolicySettingsChange}
                        onValidationUpdate={onPolicySettingValidationChange}
                        reoccurrenceSettingsDisabled={reoccurrenceSettingsDisabled}
                        reoccurrenceSettingsDisabledNotification='Cannot alter reoccurrence settings as the policy has already started.'
                    />
                );
            case 'Additional Settings':
                return (
                    <PolicyAdditionalSettings
                        saving={disableEditing}
                        loading={loading}
                        policy={tabsProps.policySettings}
                        onSettingsUpdate={onPolicySettingsChange}
                        onValidationUpdate={onPolicySettingValidationChange}
                        reoccurrenceSettingsDisabled={reoccurrenceSettingsDisabled}
                        reoccurrenceSettingsDisabledNotification='Cannot alter start date settings as the policy has already started.'
                    />
                );
            case 'Documents':
                return (
                    <PolicyDocumentSettings
                        disabled={disableEditing}
                        loading={loading}
                        documents={tabsProps.policySettings}
                        isDataDirty={tabsProps.isDataDirty}
                        onSettingsUpdate={onPolicySettingsChange}
                        onValidationUpdate={onPolicySettingValidationChange}
                    />
                );

            default:
                return (
                    <div>Invalid section</div>
                )
        }
    }, [saving, loading, disabled, reoccurrenceSettingsDisabled]);

    return (
        <div className="row">
            <h2 id="header-new-location">Edit {props.show}</h2>
            <NotificationInline
                id="notification-settings-disabled"
                loading={loading}
                show={disabled}
                text="The policy has ended so can no longer be edited."
                intent="info">
            </NotificationInline>
            <div>
                <Tabs policySettings={policySettings} show={props.show} isDataDirty={isDataDirty} />

                <AlertUnsavedData
                    isDirty={isDataDirty}>
                </AlertUnsavedData>

                <ButtonSave
                    id="btn-policy-edit"
                    onClick={savePolicy}
                    disabled={saving}
                    simpleDisabled={disabled}
                    loading={loading}
                ></ButtonSave>
            </div>
        </div>
    );
}